/**
 * InterviewTemplateListPage — Issue #188.
 *
 * Read-only list of interview notice templates. Editing / creating templates
 * is out of scope for this Issue's Admin UI.
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  useHrInterviewTemplates,
  type HrInterviewTemplateDto,
} from '@/hooks/useHrIssue188';

const KIND_LABELS: Record<HrInterviewTemplateDto['kind'], string> = {
  first_interview: '初面通知',
  second_interview: '复试通知',
  online_interview: '线上面试',
  onsite_interview: '线下面试',
  reschedule: '改期通知',
  reminder: '面试提醒',
  result: '面试结果',
  onboarding_materials: '入职材料',
};

const CHANNEL_LABELS: Record<HrInterviewTemplateDto['channel'], string> = {
  sms: '短信',
  email: '邮件',
  wechat_work: '企业微信',
  in_app: '站内',
};

export default function InterviewTemplateListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.INTERVIEW_READ);

  const { items, total, loading, error, fetchTemplates } = useHrInterviewTemplates();
  const [kind, setKind] = useState<HrInterviewTemplateDto['kind'] | ''>('');
  const [enabled, setEnabled] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchTemplates({
      kind: kind || undefined,
      enabled: enabled === 'all' ? undefined : enabled === 'enabled',
      page,
      limit,
    });
  }, [canRead, kind, enabled, page, fetchTemplates]);

  if (!canRead) return <AccessDenied resource="面试通知模板" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">面试通知模板</h1>
        <p className="text-sm text-slate-500">
          模板正文中禁止使用候选人手机号 / 身份证号等明文 PII；变量请使用占位符。
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          类型
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={kind}
            onChange={(e) => {
              setKind((e.target.value as HrInterviewTemplateDto['kind']) || '');
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(Object.entries(KIND_LABELS) as [
              HrInterviewTemplateDto['kind'],
              string,
            ][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={enabled}
            onChange={(e) => {
              setEnabled(e.target.value as 'all' | 'enabled' | 'disabled');
              setPage(1);
            }}
          >
            <option value="all">全部</option>
            <option value="enabled">启用</option>
            <option value="disabled">停用</option>
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2 w-40">编码</th>
              <th className="px-3 py-2 w-32">名称</th>
              <th className="px-3 py-2 w-32">类型</th>
              <th className="px-3 py-2 w-24">渠道</th>
              <th className="px-3 py-2 w-20">状态</th>
              <th className="px-3 py-2">主题</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 font-mono text-xs">{row.templateCode}</td>
                <td className="px-3 py-2">{row.displayName}</td>
                <td className="px-3 py-2">{KIND_LABELS[row.kind]}</td>
                <td className="px-3 py-2">{CHANNEL_LABELS[row.channel]}</td>
                <td className="px-3 py-2">{row.enabled ? '启用' : '停用'}</td>
                <td className="px-3 py-2 text-slate-600">{row.subject ?? '—'}</td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  没有模板
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
